import { FormsElementTypeEnum } from 'generated/resources/graphql'
import { makeObservable, observable } from 'mobx'

import { Block, BlockConfig } from './Block'

/**
 * Represents a form element (question) in a quiz
 */
export interface FormElement {
  id?: string | null
  label: string
  description?: string | null
  type: FormsElementTypeEnum
  position: number
  mandatory: boolean
  hasCorrectAnswer?: boolean
  options?: Array<{
    id?: string | null
    label: string
    value: string
    isCorrect?: boolean
  }>
}

/**
 * Represents the form data for a quiz block
 */
export interface QuizForm {
  id?: string | null
  title: string
  description?: string | null
  resourceId?: string | null
  resourceType: string
  formType?: string | null
  elements: FormElement[]
  configuration?: {
    allowedElements?: FormsElementTypeEnum[]
    retryable?: boolean
    scorable: boolean
  }
}

export type QuizBlockConfig = BlockConfig & {
  form?: QuizForm
}

/**
 * Quiz Block - A content block that contains a quiz (form with questions)
 */
export class QuizBlock extends Block {
  /**
   * The form data containing quiz questions
   */
  form?: QuizForm

  constructor(config: QuizBlockConfig = {}) {
    super(config)

    this.form = config.form

    makeObservable(this, {
      form: observable,
    })
  }

  /**
   * Creates a deep copy of this quiz block
   */
  clone(): QuizBlock {
    return new QuizBlock({
      uuid: this.uuid,
      title: this.title,
      form: this.form
        ? {
            id: this.form.id,
            title: this.form.title,
            description: this.form.description,
            resourceId: this.form.resourceId,
            resourceType: this.form.resourceType,
            formType: this.form.formType,
            elements: this.form.elements.map((element) => ({
              id: element.id,
              label: element.label,
              description: element.description,
              type: element.type,
              position: element.position,
              mandatory: element.mandatory,
              hasCorrectAnswer: true,
              options: element.options?.map((opt) => ({
                id: opt.id,
                label: opt.label,
                value: opt.value,
                isCorrect: opt.isCorrect,
              })),
            })),
            configuration: this.form.configuration
              ? {
                  allowedElements: this.form.configuration.allowedElements,
                  retryable: this.form.configuration.retryable,
                  scorable: this.form.configuration.scorable,
                }
              : undefined,
          }
        : undefined,
    })
  }

  /**
   * Serializes the quiz block for the processContent mutation
   */
  toJSON() {
    return {
      title: this.title,
      uuid: this.uuid,
      type: 'quiz',
      typeQuiz: this.form
        ? {
            form: {
              id: this.form.id || null,
              title: this.form.title,
              description: this.form.description || null,
              resourceId: this.form.resourceId || null,
              resourceType: this.form.resourceType,
              formType: this.form.formType || 'quiz',
              elements: this.form.elements,
            },
          }
        : undefined,
    }
  }
}
